class CustomCarousel extends HTMLElement {
  constructor() {
    super()
    this.selectors = {
      loop: "data-loop",
      scrollCount: "[data-scroll-num]",
      dragFree: "data-drag-free",
      align: "data-align",
      controls: "data-controls",
      autoplay: "data-autoplay",
      autoplayInterval: "data-autoplay-interval",
      carouselContainer: ".carousel-container",
      progressBar: ".progress-bar",
      dotsContainer: ".dots-container",
    }
  }

  connectedCallback() {
    const loop = this.hasAttribute(this.selectors.loop)
    const scrollCount = this.getAttribute(this.selectors.scrollCount) ?? 1
    const controls = this.hasAttribute(this.selectors.controls)
    const autoplay = this.hasAttribute(this.selectors.autoplay)
    const autoplayInterval =
      parseInt(this.getAttribute(this.selectors.autoplayInterval)) || 5000
    const axis = this.getAttribute("data-axis") ?? "x"
    const mobileOnly = this.hasAttribute("data-mobile-only")

    this.carousel = this.querySelector(".carousel-viewport")
    const dragFree = this.hasAttribute("data-drag-free")
    const fade = this.hasAttribute("fade")
    const align = this.getAttribute("data-align") || "start"

    const plugins = fade ? [EmblaCarouselFade()] : []
    if (autoplay) {
      this.autoplayPlugin = EmblaCarouselAutoplay({ delay: autoplayInterval })
      plugins.push(this.autoplayPlugin)
    }

    const embla = EmblaCarousel(
      this.carousel,
      {
        loop: loop,
        align: align,
        dragFree: dragFree,
        skipSnaps: true,
        axis: axis,
        slidesToScroll: parseInt(scrollCount),
        // Carousel only on mobile: Embla stays inactive (no transform, no drag)
        // above the `lg` breakpoint and re-inits itself when it is crossed.
        active: !mobileOnly,
        breakpoints: mobileOnly
          ? { "(max-width: 1023px)": { active: true } }
          : {},
      },
      plugins
    )

    if (this.hasAttribute("data-scale")) {
      const removeTweenScale = setupTweenScale(embla)
    }
    this.embla = embla

    if (controls) {
      this.setupControls()
    }

    this.setupDots()
    this.setupProgressBar()

    this.embla.on("select", () => {
      this.updateProgressBar()
      this.updateDots()
    })

    this.embla.on("scroll", () => {
      this.updateProgressBar()
    })

    const slides = this.embla.slideNodes()

    const addClass = () => {
      const nodeIndex = embla.selectedScrollSnap()
      slides.forEach((slide, index) => {
        slide.classList.toggle("is-selected", nodeIndex === index)
      })
    }

    if (slides.length) slides[0].classList.add("is-selected")

    embla.on("select", addClass)
    if (mobileOnly) embla.on("reInit", addClass)

    const prevBtnNode = this.querySelector(".prev-button")
    const nextBtnNode = this.querySelector(".next-button")

    this.slides = slides

    if (prevBtnNode && nextBtnNode) {
      const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
        embla,
        prevBtnNode,
        nextBtnNode
      )

      embla.on("destroy", removePrevNextBtnsClickHandlers)
    }
  }

  disconnectedCallback() {
    if (this.progressFrame) cancelAnimationFrame(this.progressFrame)
    this.embla.destroy()
    window.removeEventListener("resize", this.destroyDesktopCarousel)
    window.removeEventListener("resize", this.destroyMobileCarousel)
  }

  setupControls() {
    const prevBtnNodes = Array.from(
      this.querySelectorAll(".embla__button--prev")
    )
    const nextBtnNodes = Array.from(
      this.querySelectorAll(".embla__button--next")
    )

    prevBtnNodes.forEach((prevBtnNode, index) => {
      const nextBtnNode = nextBtnNodes[index]
      if (nextBtnNode) {
        const removePrevNextBtnsClickHandlers =
          this.addPrevNextBtnsClickHandlers(
            this.embla,
            prevBtnNode,
            nextBtnNode
          )
        this.embla.on("destroy", removePrevNextBtnsClickHandlers)
      }
    })
  }

  setupDots() {
    const dotsContainer = this.querySelector(this.selectors.dotsContainer)
    if (!dotsContainer) return

    const slides = this.embla.slideNodes()
    dotsContainer.innerHTML = "" // Clear any existing dots

    slides.forEach((_, index) => {
      const dot = document.createElement("button")
      dot.className = "dot"
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`)
      dot.addEventListener("click", () => this.embla.scrollTo(index))
      dotsContainer.appendChild(dot)
    })

    this.updateDots()
  }

  updateDots() {
    const dotsContainer = this.querySelector(this.selectors.dotsContainer)
    if (!dotsContainer) return

    const selectedIndex = this.embla.selectedScrollSnap()
    dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
      dot.classList.toggle("active", index === selectedIndex)
    })
  }

  setupProgressBar() {
    this.progressBars = Array.from(
      this.querySelectorAll(this.selectors.progressBar)
    )
    if (this.progressBars.length === 0) return

    const remeasure = () => {
      this.measureProgressBaseline()
      this.updateProgressBar()
    }

    this.embla.on("reInit", remeasure)

    requestAnimationFrame(remeasure)
  }

  measureProgressBaseline() {
    const totalSlides = this.embla.slideNodes().length
    this.progressBaseline = totalSlides > 0 ? 1 / totalSlides : 1
  }

  updateProgressBar() {
    if (!this.progressBars || this.progressBars.length === 0) return
    
    if (this.progressFrame) return

    this.progressFrame = requestAnimationFrame(() => {
      this.progressFrame = null

      if (this.progressBaseline === undefined) this.measureProgressBaseline()

      const baseline = this.progressBaseline
      const scrollProgress = Math.min(
        Math.max(this.embla.scrollProgress(), 0),
        1
      )
      const currentProgress =
        (baseline + scrollProgress * (1 - baseline)) * 100

      this.progressBars.forEach((progressBar) => {
        progressBar.style.width = `${currentProgress}%`
      })
    })
  }

  addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
    const togglePrevNextBtnsState = () => {
      if (emblaApi.canScrollPrev()) prevBtn.removeAttribute("disabled")
      else prevBtn.setAttribute("disabled", "disabled")

      if (emblaApi.canScrollNext()) nextBtn.removeAttribute("disabled")
      else nextBtn.setAttribute("disabled", "disabled")
    }

    emblaApi
      .on("select", togglePrevNextBtnsState)
      .on("init", togglePrevNextBtnsState)
      .on("reInit", togglePrevNextBtnsState)

    return () => {
      prevBtn.removeAttribute("disabled")
      nextBtn.removeAttribute("disabled")
    }
  }

  addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const scrollPrev = (e) => {
      emblaApi.scrollPrev()
    }
    const scrollNext = (e) => {
      emblaApi.scrollNext()
    }
    prevBtn.addEventListener("click", scrollPrev)
    nextBtn.addEventListener("click", scrollNext)

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
      emblaApi,
      prevBtn,
      nextBtn
    )

    return () => {
      removeTogglePrevNextBtnsActive()
      prevBtn.removeEventListener("click", scrollPrev)
      nextBtn.removeEventListener("click", scrollNext)
    }
  }
}

const TWEEN_FACTOR_BASE = 0.15
let tweenFactor = 0
let tweenNodes = []

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max)

const setTweenNodes = (emblaApi) => {
  tweenNodes = emblaApi.slideNodes().map((slideNode) => {
    return slideNode.querySelector(".embla__slide__number")
  })
}

const setTweenFactor = (emblaApi) => {
  tweenFactor = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
}

const tweenScale = (emblaApi, eventName) => {
  const engine = emblaApi.internalEngine()
  const scrollProgress = emblaApi.scrollProgress()
  const slidesInView = emblaApi.slidesInView()
  const isScrollEvent = eventName === "scroll"

  emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
    let diffToTarget = scrollSnap - scrollProgress
    const slidesInSnap = engine.slideRegistry[snapIndex]

    slidesInSnap.forEach((slideIndex) => {
      if (isScrollEvent && !slidesInView.includes(slideIndex)) return

      if (engine.options.loop) {
        engine.slideLooper.loopPoints.forEach((loopItem) => {
          const target = loopItem.target()

          if (slideIndex === loopItem.index && target !== 0) {
            const sign = Math.sign(target)

            if (sign === -1) {
              diffToTarget = scrollSnap - (1 + scrollProgress)
            }
            if (sign === 1) {
              diffToTarget = scrollSnap + (1 - scrollProgress)
            }
          }
        })
      }

      const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor)
      const scale = numberWithinRange(tweenValue, 0, 1).toString()
      const tweenNode = tweenNodes[slideIndex]
      tweenNode.style.transform = `scale(${scale})`
    })
  })
}

const setupTweenScale = (emblaApi) => {
  setTweenNodes(emblaApi)
  setTweenFactor(emblaApi)
  tweenScale(emblaApi)

  emblaApi
    .on("reInit", setTweenNodes)
    .on("reInit", setTweenFactor)
    .on("reInit", tweenScale)
    .on("scroll", tweenScale)
    .on("slideFocus", tweenScale)

  return () => {
    tweenNodes.forEach((slide) => slide.removeAttribute("style"))
  }
}

if (!customElements.get("custom-carousel")) {
  customElements.define("custom-carousel", CustomCarousel)
}
